import { getFormulaFunctionDefinitions } from 'automation-ui-formula-editor/formula-editor/FormulaFunctionDefinitions';
import ExpressionTypes from 'automation-ui-formula-editor/formula-editor/grammar/constants/ExpressionTypes';
import FieldSources from 'automation-ui-formula-editor/formula-editor/grammar/constants/FieldSources';
export function isStringLiteralExpression(expression) {
  return expression.type === ExpressionTypes.STRING_LITERAL;
}
export function isNumericLiteralExpression(expression) {
  return expression.type === ExpressionTypes.NUMERIC_LITERAL;
}

/* ------------------------------------------------------------------ */
/* Main expression type categories: Function call, literal, reference */
/* ------------------------------------------------------------------ */

export function isFunctionCallExpression(expression) {
  return expression.type === ExpressionTypes.FUNCTION_CALL;
}
export function isLiteralExpression(expression) {
  return isStringLiteralExpression(expression) || isNumericLiteralExpression(expression);
}
export function isUnaryExpression(expression) {
  return expression.type === ExpressionTypes.NEGATIVE || expression.type === ExpressionTypes.POSITIVE;
}
export function isBinaryExpression(expression) {
  return expression.type === ExpressionTypes.ADD || expression.type === ExpressionTypes.SUBTRACT || expression.type === ExpressionTypes.MULTIPLY || expression.type === ExpressionTypes.DIVIDE;
}
export function isIdentifierExpression(expression) {
  return expression.type === ExpressionTypes.IDENTIFIER;
}

// Certain kinds of expressions may expect to fail basic type checking cases. We can skip those checks since they are invalid in other ways. IE plain identifiers are not allowed, and partial function calls are syntactially invalid. Therefore we do not need to check their return types
export const shouldSkipStrictTypeChecking = expression => {
  return expression.type === ExpressionTypes.IDENTIFIER || expression.type === ExpressionTypes.PARTIAL_FUNCTION_CALL;
};
export function isFieldExpression(expression) {
  return expression.type === ExpressionTypes.FIELD;
}
export const isReferenceExpression = expression => {
  switch (expression.type) {
    case ExpressionTypes.IDENTIFIER:
    case ExpressionTypes.FIELD:
      return true;
    default:
      return false;
  }
};
export const expressionToList = expression => {
  if (!expression) {
    return [];
  }
  return [expression, ...[...(expression.arguments || []), ...(expression.elements || []), expression.argument, expression.left, expression.right].filter(subExpression => !!subExpression).flatMap(expressionToList)];
};
export const isExpressionValidForExpressionMode = (expression, expressionMode = {}) => {
  const expressionList = expressionToList(expression);
  if (!expressionList.length || !Object.keys(expressionList[0]).length) {
    return true;
  }
  const {
    allowedExpressionTypes
  } = expressionMode;

  // when no allow list is defined, assume the expression is valid
  if (!allowedExpressionTypes) {
    return true;
  }
  const isValid = !expressionList.some(_expression => !allowedExpressionTypes.includes(_expression.type));
  return isValid;
};

/*
 * Simple expression has the following criteria
 * Primary expression must be a function call
 * first argument is a field, additional arguments must be literals
 */
export const isSimpleHubLExpression = expression => {
  const expressionList = expressionToList(expression);
  const formulaFunctionDefinitions = getFormulaFunctionDefinitions();
  return expressionList.length && (!Object.keys(expressionList[0]).length /* empty expression is simple */ || expressionList[0].type === ExpressionTypes.FUNCTION_CALL && !(formulaFunctionDefinitions[expressionList[0].name] && formulaFunctionDefinitions[expressionList[0].name].isCustom) && expressionList[0].arguments.every((expr, idx) => idx === 0 ? expr.type === ExpressionTypes.FIELD && expr.source !== FieldSources.DATA_SOURCES_PANEL /* Temporary as of 12/13/23: HFI not integrated with data source panel, so we must render these as custom formulas and never simple formulas */ || 'value' in expr && expr.value === '' : expr.type === ExpressionTypes.STRING_LITERAL || expr.type === ExpressionTypes.NUMERIC_LITERAL) && !expressionList.some(expr => expr.type === ExpressionTypes.STRING_LITERAL && 'value' in expr && expr.value.includes('"')));
};
export const getUniqueFunctionNames = parsedExpression => {
  try {
    const functionNameList = expressionToList(parsedExpression).filter(expr => expr.type === ExpressionTypes.FUNCTION_CALL).map(expr => expr.name) || [];
    return [...new Set(functionNameList)];
  } catch (_unused) {
    return [];
  }
};
export const getUniqueExpressionTypes = parsedExpression => {
  try {
    const expressionTypesList = expressionToList(parsedExpression).map(expr => expr.type) || [];
    return [...new Set(expressionTypesList)];
  } catch (_unused2) {
    return [];
  }
};